import { useState } from 'react'
import axios from 'axios'
import { useAuth } from '../context/AuthContext'

// Wraps the app and forces users whose email is not yet verified into a
// dead-end screen with a "resend" button. OAuth users have
// email_confirmed_at populated by Supabase, so they bypass this naturally.
export default function VerifyEmailGate({ children }) {
  const { user, emailVerified, getToken, signOut } = useAuth()
  const [sending, setSending] = useState(false)
  const [sentAt, setSentAt] = useState(null)
  const [error, setError] = useState(null)

  if (!user || emailVerified) return children

  async function handleResend() {
    setSending(true)
    setError(null)
    try {
      const token = await getToken()
      await axios.post('/api/user/resend-verification', {}, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setSentAt(Date.now())
    } catch (err) {
      setError(err.response?.data?.error ?? 'Could not send verification email. Try again in a moment.')
    } finally {
      setSending(false)
    }
  }

  // Disable the resend button for 60s after a successful send to avoid
  // clobbering the client-side rate limiter message.
  const cooldownLeft = sentAt ? Math.max(0, 60 - Math.floor((Date.now() - sentAt) / 1000)) : 0

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24, background: 'var(--bg)',
    }}>
      <div style={{
        width: '100%', maxWidth: 440, padding: 32, borderRadius: 16,
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
      }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
          Verify your email
        </h1>
        <p style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 12, lineHeight: 1.5 }}>
          We sent a confirmation link to <strong style={{ color: 'var(--text)' }}>{user.email}</strong>.
          Click the link in that email to activate your account. You won't be able to use Verse until you do.
        </p>
        <p style={{ fontSize: 13, color: 'var(--text-faint)', marginTop: 8 }}>
          Can't find it? Check your spam folder, or request a fresh one below.
        </p>

        {sentAt && !error && (
          <p style={{ fontSize: 13, color: 'var(--green)', marginTop: 16 }}>
            Sent! Check your inbox.
          </p>
        )}
        {error && (
          <p style={{ fontSize: 13, color: 'var(--red)', marginTop: 16 }}>{error}</p>
        )}

        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <button
            className="btn btn-primary"
            onClick={handleResend}
            disabled={sending || cooldownLeft > 0}
            style={{ flex: 1 }}
          >
            {sending ? 'Sending…' : cooldownLeft > 0 ? `Resend in ${cooldownLeft}s` : 'Resend email'}
          </button>
          <button
            className="btn btn-outline"
            onClick={() => signOut()}
            style={{ flex: 1 }}
          >
            Sign out
          </button>
        </div>

        <p style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: 24, textAlign: 'center' }}>
          Already verified? Refresh the page.
        </p>
      </div>
    </div>
  )
}
